export interface IndianMobileResult {
  ok: boolean;
  /** Normalized E.164 form, e.g. +919876543210 */
  e164?: string;
  /** 10-digit national number, e.g. 9876543210 */
  national?: string;
  error?: string;
}

/**
 * Validate and normalize an Indian mobile number.
 * Accepts optional +91 / 91 / 0 prefixes and spaces, hyphens, or parentheses.
 * Mobile numbers are 10 digits starting with 6-9.
 */
export function parseIndianMobile(input: string): IndianMobileResult {
  const raw = input.trim();
  if (!raw) return { ok: false, error: 'Phone number is required' };

  const digits = raw.replace(/\D/g, '');

  let national = digits;
  if (national.length === 12 && national.startsWith('91')) {
    national = national.slice(2);
  } else if (national.length === 11 && national.startsWith('0')) {
    national = national.slice(1);
  }

  if (!/^[6-9]\d{9}$/.test(national)) {
    return { ok: false, error: 'Enter a valid 10-digit Indian mobile number' };
  }

  return { ok: true, national, e164: `+91${national}` };
}

export interface PhoneLinks {
  /** tel: link, e.g. tel:+919876543210 */
  tel: string;
  /** WhatsApp chat link, e.g. https://wa.me/919876543210 */
  whatsapp: string;
}

/**
 * Call and WhatsApp links for a stored phone number. Indian mobiles (with or without +91 / 91 / 0)
 * become +91XXXXXXXXXX; other numbers written with a leading + and 8–15 digits are used as they are.
 * Anything else gets no links (shown as plain text).
 */
export function phoneLinks(phone: string | null | undefined): PhoneLinks | null {
  const raw = (phone ?? '').trim();
  if (!raw) return null;
  const indian = parseIndianMobile(raw);
  let digits: string | null = null;
  if (indian.ok && indian.national) digits = `91${indian.national}`;
  else if (raw.startsWith('+')) {
    const intl = raw.replace(/\D/g, '');
    if (intl.length >= 8 && intl.length <= 15) digits = intl;
  }
  return digits ? { tel: `tel:+${digits}`, whatsapp: `https://wa.me/${digits}` } : null;
}
